/**
 * 日期工具类
 * 
 * P0问题修复：解决日期时区问题
 * 
 * 问题：使用 new Date().toISOString().split('T')[0] 会有时区问题
 * 例如：北京时间晚上8点，toISOString()会返回UTC时间（中午12点），导致日期差一天
 */

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

/**
 * 获取当前日期（YYYY-MM-DD格式）
 * 解决时区问题
 */
export const getCurrentDate = (): string => {
  return dayjs().format('YYYY-MM-DD');
};

/**
 * 获取当前时间（YYYY-MM-DD HH:mm:ss格式）
 */
export const getCurrentDateTime = (): string => {
  return dayjs().format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 格式化日期
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * 格式化日期时间
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 格式化为显示日期（中文格式）
 */
export const formatDisplayDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return dayjs(date).format('YYYY年MM月DD日');
};

/**
 * 格式化为显示日期时间（中文格式）
 */
export const formatDisplayDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return dayjs(date).format('YYYY年MM月DD日 HH:mm');
};

/**
 * 获取相对时间（例如：3天前）
 */
export const getRelativeTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const now = dayjs();
  const target = dayjs(date);
  const diffDays = now.diff(target, 'day');
  
  if (diffDays === 0) {
    const diffHours = now.diff(target, 'hour');
    if (diffHours === 0) {
      const diffMinutes = now.diff(target, 'minute');
      if (diffMinutes === 0) {
        return '刚刚';
      }
      return `${diffMinutes}分钟前`;
    }
    return `${diffHours}小时前`;
  }
  
  if (diffDays < 7) {
    return `${diffDays}天前`;
  }
  
  if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)}周前`;
  }
  
  if (diffDays < 365) {
    return `${Math.floor(diffDays / 30)}个月前`;
  }
  
  return `${Math.floor(diffDays / 365)}年前`;
};

/**
 * 解析日期字符串
 */
export const parseDate = (dateStr: string): Date | null => {
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * 判断是否为今天
 */
export const isToday = (date: string | Date): boolean => {
  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * 判断是否为本周
 */
export const isThisWeek = (date: string | Date): boolean => {
  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');
  return dayjs(date).isAfter(startOfWeek) && dayjs(date).isBefore(endOfWeek);
};

/**
 * 判断是否为本月
 */
export const isThisMonth = (date: string | Date): boolean => {
  return dayjs(date).isSame(dayjs(), 'month');
};

/**
 * 获取日期范围
 */
export const getDateRange = (
  type: 'today' | 'week' | 'month' | 'quarter' | 'year'
): [string, string] => {
  const now = dayjs();
  
  switch (type) {
    case 'today':
      return [now.format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case 'week':
      return [
        now.startOf('week').format('YYYY-MM-DD'),
        now.endOf('week').format('YYYY-MM-DD'),
      ];
    case 'month':
      return [
        now.startOf('month').format('YYYY-MM-DD'),
        now.endOf('month').format('YYYY-MM-DD'),
      ];
    case 'quarter':
      return [
        now.startOf('quarter').format('YYYY-MM-DD'),
        now.endOf('quarter').format('YYYY-MM-DD'),
      ];
    case 'year':
      return [
        now.startOf('year').format('YYYY-MM-DD'),
        now.endOf('year').format('YYYY-MM-DD'),
      ];
    default:
      return [now.format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
  }
};

/**
 * 计算两个日期之间的天数
 */
export const getDaysBetween = (startDate: string | Date, endDate: string | Date): number => {
  return dayjs(endDate).diff(dayjs(startDate), 'day');
};

/**
 * 添加天数
 */
export const addDays = (date: string | Date, days: number): string => {
  return dayjs(date).add(days, 'day').format('YYYY-MM-DD');
};

/**
 * 减去天数
 */
export const subtractDays = (date: string | Date, days: number): string => {
  return dayjs(date).subtract(days, 'day').format('YYYY-MM-DD');
};

/**
 * 获取月份的开始和结束日期
 */
export const getMonthRange = (year: number, month: number): [string, string] => {
  const date = dayjs(`${year}-${month}-01`);
  return [
    date.startOf('month').format('YYYY-MM-DD'),
    date.endOf('month').format('YYYY-MM-DD'),
  ];
};

/**
 * 验证日期格式
 */
export const isValidDate = (dateStr: string): boolean => {
  return dayjs(dateStr).isValid();
};

/**
 * 比较日期
 */
export const compareDate = (date1: string | Date, date2: string | Date): number => {
  const d1 = dayjs(date1);
  const d2 = dayjs(date2);
  
  if (d1.isBefore(d2)) return -1;
  if (d1.isAfter(d2)) return 1;
  return 0;
};

/**
 * 获取年龄
 */
export const getAge = (birthDate: string | Date): number => {
  return dayjs().diff(dayjs(birthDate), 'year');
};

/**
 * 获取工作日数量（排除周末）
 */
export const getWorkDays = (startDate: string | Date, endDate: string | Date): number => {
  let count = 0;
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  
  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dayOfWeek = current.day();
    // 0是周日，6是周六
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current = current.add(1, 'day');
  }
  
  return count;
};
